import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, MapPin, Users, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/sections/Navbar';
import Footer from '../components/sections/Footer';
import { ArkansasBadge } from '../components/branding/TrustBadges';

export default function AboutPage() {
  const navigate = useNavigate();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" tabIndex={-1}>

      {/* Hero */}
      <section className="relative py-20 sm:py-28 bg-coal text-white overflow-hidden" data-testid="about-hero">
        <div className="noise-overlay" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white hover:bg-white/10 mb-6 -ml-2 gap-2"
            data-testid="about-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex flex-wrap gap-3 mb-6">
              <Badge variant="secondary" className="font-mono text-xs tracking-wider uppercase bg-white/10 text-white border-white/20">
                EST. 1999
              </Badge>
              <Badge variant="secondary" className="font-mono text-xs tracking-wider uppercase bg-white/10 text-white border-white/20">
                <Shield className="w-3 h-3 mr-1" /> Safety-Driven
              </Badge>
              <Badge variant="secondary" className="font-mono text-xs tracking-wider uppercase bg-white/10 text-white border-white/20">
                <MapPin className="w-3 h-3 mr-1" /> Conway, AR
              </Badge>
            </div>
            <h1 className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6">
              Safety <span className="italic font-normal text-forest-2">&amp;</span> Experience<br />
              <span className="italic font-normal text-forest-2">Since</span> 1999.
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              Specialized civil, mechanical, and process construction for municipal, industrial, energy, and government clients across the United States.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 sm:py-20 lg:py-28 bg-background" ref={ref}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Our Story</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-6">
                Built on trust, driven by safety.
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Since 1999, Ozark Mountain Construction has specialized in delivering
                  civil, mechanical, and process construction for municipal, industrial,
                  energy, and government clients across the United States. Our expertise
                  centers on the reinforced concrete foundations, structural systems,
                  underground utilities, and process infrastructure that support elevated
                  water tanks, ground storage tanks, pump stations, water treatment
                  facilities, and other critical infrastructure.
                </p>
                <p>
                  Our crews routinely perform work in some of the nation&rsquo;s most
                  demanding environments, including military installations, power
                  generation facilities, chemical and ammonia plants, petroleum terminals,
                  manufacturing facilities, and other high-security, mission-critical sites
                  where precision, quality, and safety are essential.
                </p>
                <p>
                  Every project reflects our commitment to disciplined execution,
                  exceptional craftsmanship, and long-term client partnerships. We believe
                  lasting relationships are built through reliability, accountability, and
                  an uncompromising commitment to delivering work safely and to the highest
                  standards.
                </p>
                <p>
                  Safety is embedded in every phase of construction. Through comprehensive
                  preconstruction planning, job hazard analyses, daily pre-task meetings,
                  continuous training, and rigorous field oversight, we proactively manage
                  risk and foster a culture where every employee is empowered to protect
                  themselves, their teammates, and the clients we serve.
                </p>
              </div>

              {/* ISNetworld badge */}
              <div className="mt-8 flex items-center gap-4 p-4 rounded-xl bg-muted/50 border border-border">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-display font-semibold text-sm text-foreground">ISNetworld Member</p>
                  <p className="text-xs text-muted-foreground">Creating safer work environments and lasting partnerships</p>
                </div>
              </div>

              {/* Arkansas-based pride mark */}
              <div className="mt-6 flex items-center gap-5" data-testid="about-arkansas-badge">
                <div className="text-primary shrink-0">
                  <ArkansasBadge size={96} />
                </div>
                <div>
                  <p className="font-display font-semibold text-sm text-foreground">Proudly Arkansas-Based</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Headquartered in Conway, Arkansas — building foundations nationwide since 1999.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Values */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-6"
            >
              <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Our Values</p>
              {[
                { icon: Shield, title: 'Safety First', desc: 'Safety is embedded in every phase of construction — managed through daily pre-task planning, job hazard analyses, continuous training, and rigorous field oversight.' },
                { icon: Users, title: 'Client Partnership', desc: 'We build long-term relationships through reliability, accountability, and disciplined execution on every project.' },
                { icon: CheckCircle2, title: 'Quality Assurance', desc: 'Every project meets the highest standards. A+ BBB Rating reflects our commitment to excellence.' },
                { icon: MapPin, title: 'Nationwide Capability', desc: 'We build to suit in any climate, location, and topography across the United States.' },
              ].map((value) => {
                const Icon = value.icon;
                return (
                  <div key={value.title} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-sm text-foreground mb-1">{value.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{value.desc}</p>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Purpose / Standard / Vision — mirrors the homepage heritage graphic */}
      <section
        className="py-12 sm:py-16 lg:py-20 bg-bone-2 border-t border-border"
        data-testid="about-values-graphic-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="rounded-xl overflow-hidden border border-foreground/10 shadow-elev-2">
            <img
              src="/brand/heritage-values.jpg"
              alt="Ozark Mountain Construction values — Our Purpose: build infrastructure people can depend on. Our Standard: do the work right, stand behind it, build it to last. Our Vision: build a company whose reputation lasts as long as the infrastructure we leave behind."
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
}
